import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { PageLoad } from '../components/ui';
import './HostSpecialistDashboard.css';
import AlertStudents from '../components/AlertStudents';

const AMBER = '#F5A623';

/* Icon helper (mirrors DashboardPage) */
function DI({ d, size = 18, stroke = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  );
}

export default function HostSpecialistDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/host-specialist')
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoad />;
  if (!data) return <div style={{ padding: 24, color: 'var(--ink-2)' }}>Could not load the Host Specialist dashboard.</div>;

  const { placed, notPlaced, total, placementRate, byPC, topCities, programPlacement, alerts, demandTimeline, reviewRequests } = data;

  const firstName = user?.full_name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const highAlerts = alerts.filter(a => a.severity === 'high').length;

  return (
    <>
      {/* ── Hero: Placed vs Not-Yet ───────────────────────────────────────── */}
      <div className="h-hero">
        <div className="h-hero-decor" aria-hidden="true">
          <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
            <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
              <circle cx="720" cy="160" r="80" /><circle cx="720" cy="160" r="140" />
              <circle cx="720" cy="160" r="200" /><circle cx="720" cy="160" r="260" />
            </g>
            <g fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1">
              <circle cx="540" cy="40" r="180" /><circle cx="540" cy="40" r="240" />
            </g>
          </svg>
        </div>
        <div className="h-hero-inner">
          <div className="h-hero-top">
            <div>
              <div className="h-hero-crumb">
                <span>{dateStr}</span><span className="h-crumb-sep">·</span>
                <span>Host Specialist Dashboard</span>
              </div>
              <h1 className="h-hero-title">
                {greeting}, <span className="h-hero-accent">{firstName}</span>.
              </h1>
              <p className="h-hero-lede">
                {placed} of {total} students placed across all coordinators
                {alerts.length > 0 ? ` · ${alerts.length} host-supply gap${alerts.length !== 1 ? 's' : ''} to close` : ' · supply is keeping up'}.
              </p>
            </div>
            <div className="h-hero-actions">
              <Link to="/host-prospects" className="h-btn-accent" style={{ background: AMBER, textDecoration: 'none', color: '#0B1F2E' }}>
                <DI d="M12 5v14M5 12h14" size={16} /> Add Prospect
              </Link>
            </div>
          </div>

          <div className="h-stats dash-stats-4">
            <Kpi eyebrow="PLACED" value={placed} label="Students on placement" delta={`${placementRate}% of all students`} fill={placementRate} icon="check" />
            <Kpi eyebrow="NOT YET PLACED" value={notPlaced} label="Still need a host" delta={`${total} total students`} fill={total ? Math.round(notPlaced / total * 100) : 0} icon="clock" />
            <Kpi eyebrow="PLACEMENT RATE" value={`${placementRate}%`} label="Across all programs" delta="placed vs. total" fill={placementRate} icon="reports" />
            <Kpi eyebrow="SUPPLY GAPS" value={alerts.length} label="Program / city shortfalls" delta={highAlerts > 0 ? `${highAlerts} urgent` : 'all clear'} fill={alerts.length ? Math.min(100, alerts.length * 20) : 0} icon="alert" />
          </div>
        </div>
      </div>

      <div className="h-content">
        <div className="h-content-inner">

          {/* ── Host review requests (action required) ─────────────────────── */}
          <ReviewRequests rows={reviewRequests} />

          {/* ── Alert bar: own full-width row ──────────────────────────────── */}
          <AlertBar alerts={alerts} />

          {/* ── Top cities + Program placement ─────────────────────────────── */}
          <div className="dash-grid-new hs-grid">
            <div className="dash-col-main">
              <Widget title="Placement by program" sub="Share of each program's students currently placed">
                <ProgramPlacement rows={programPlacement} />
              </Widget>

              <Widget title="Placed by coordinator" sub="Placed vs. not-yet, per program coordinator">
                <PCBreakdown rows={byPC} />
              </Widget>
            </div>

            <div className="dash-col-side">
              <Widget title="Top 5 cities by placement" sub="Where students are placed (host location)">
                <TopCities rows={topCities} />
              </Widget>

              <Widget title="Upcoming placement demand" sub="Unplaced students by start window" className="hs-fill-card">
                <DemandTimeline rows={demandTimeline} />
              </Widget>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

/* ── KPI card ─────────────────────────────────────────────────────────────── */
function Kpi({ eyebrow, value, label, delta, fill, icon }) {
  const iconPath = {
    check:   <g><path d="M20 6L9 17l-5-5" /></g>,
    clock:   <g><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></g>,
    reports: <g><path d="M3 20h18" /><path d="M6 20V10M11 20V4M16 20v-7" /></g>,
    alert:   <g><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></g>,
  };
  return (
    <div className="h-stat-card">
      <div className="h-stat-top">
        <span className="h-stat-eyebrow" style={{ color: AMBER }}>{eyebrow}</span>
        <span className="dash-stat-ico"><DI d={iconPath[icon] || iconPath.reports} size={14} /></span>
      </div>
      <div className="h-stat-num">{value}</div>
      <div className="h-stat-label">{label}</div>
      <div className="h-stat-bar"><div className="h-stat-bar-fill" style={{ width: `${Math.min(100, fill || 0)}%`, background: AMBER }} /></div>
      <div className="dash-stat-delta"><span className="dash-delta-pill">{delta}</span></div>
    </div>
  );
}

/* ── Widget shell ─────────────────────────────────────────────────────────── */
function Widget({ title, sub, action, children, className = '' }) {
  return (
    <section className={`wgt ${className}`}>
      <header className="wgt-head">
        <div>
          <h3 className="wgt-title">{title}</h3>
          {sub && <div className="wgt-sub">{sub}</div>}
        </div>
        {action}
      </header>
      <div className="wgt-body">{children}</div>
    </section>
  );
}

/* ── Host review requests (coordinators flagged as not accepting) ─────────── */
function ReviewRequests({ rows }) {
  if (!rows || rows.length === 0) return null;
  const BLUE = '#1F87E5';
  return (
    <section className="wgt hs-alert-card" style={{ borderLeftColor: BLUE }}>
      <header className="wgt-head">
        <div>
          <h3 className="wgt-title hs-alert-title">
            <span style={{ color: BLUE, display: 'inline-flex' }}>
              <DI d={<g><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></g>} size={17} />
            </span>
            Host review requests
            <span className="hs-alert-badge" style={{ color: BLUE, background: '#E5F0FB' }}>{rows.length}</span>
          </h3>
          <div className="wgt-sub">Coordinators flagged these hosts as not accepting — call the host, then confirm or reject</div>
        </div>
      </header>
      <div className="wgt-body">
        <div className="hs-alert-list">
          {rows.map(r => (
            <div key={r.id} className="hs-alert-row" style={{ background: 'var(--bg-sunken)', borderLeftColor: BLUE }}>
              <span className="hs-alert-msg">
                <strong>{r.orgName}</strong>{r.city ? ` · ${r.city}` : ''} — flagged by {r.requestedBy}
                {r.requestedAt ? ` on ${new Date(r.requestedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}
              </span>
              <Link to={`/hosts?host=${r.id}`} className="hs-alert-cta">Review host <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Alert bar (computed supply/demand gaps) ──────────────────────────────── */
function AlertBar({ alerts }) {
  const [openAlert, setOpenAlert] = useState(null);
  if (!alerts || alerts.length === 0) {
    return (
      <section className="wgt hs-alert-card is-clear">
        <div className="wgt-body">
          <div className="hs-clear-row">
            <span className="hs-clear-icon"><DI d={<g><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></g>} size={24} /></span>
            <div>
              <h4>Host supply is keeping up</h4>
              <p>No program or city has more upcoming students (next 30 days) than available host partners.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const urgent = alerts.filter(a => a.severity === 'high').length;

  return (
    <section className="wgt hs-alert-card">
      <header className="wgt-head">
        <div>
          <h3 className="wgt-title hs-alert-title">
            <span style={{ color: AMBER, display: 'inline-flex' }}>
              <DI d={<g><path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></g>} size={17} />
            </span>
            Host Supply Alerts
            <span className="hs-alert-badge">{urgent > 0 ? `${urgent} urgent` : `${alerts.length}`}</span>
          </h3>
          <div className="wgt-sub">Auto-detected: upcoming student demand (next 30 days) vs. hosts accepting students</div>
        </div>
      </header>
      <div className="wgt-body">
        <div className="hs-alert-list">
          {alerts.map((a, i) => {
            const ratio = a.demand > 0 ? Math.min(100, Math.round((a.supply / a.demand) * 100)) : 0;
            return (
              <div key={`${a.type}:${a.key}`}>
              <div className={`hs-alert-row sev-${a.severity}`}>
                <span className={`hs-tag ${a.type === 'program' ? 'is-program' : 'is-city'}`}>
                  {a.type}
                </span>
                <span className="hs-alert-msg">{a.message}</span>
                <div className="hs-gauge" title={`${a.supply} hosts for ${a.demand} students`}>
                  <div className="hs-gauge-track">
                    <div className={`hs-gauge-fill sev-${a.severity}`} style={{ width: `${ratio}%` }} />
                  </div>
                  <div className="hs-gauge-nums"><b>{a.supply}</b> / {a.demand} hosts</div>
                </div>
                <Link to="/hosts" className="hs-alert-cta">
                  Find hosts <DI d="M5 12h14M13 6l6 6-6 6" size={12} />
                </Link>
                <button type="button" className="hs-alert-cta"
                  aria-expanded={openAlert === `${a.type}:${a.key}`}
                  aria-controls={`alert-students-${i}`}
                  onClick={() => setOpenAlert(prev => prev === `${a.type}:${a.key}` ? null : `${a.type}:${a.key}`)}>
                  {openAlert === `${a.type}:${a.key}` ? 'Hide students' : `View students (${a.demand})`}
                </button>
              </div>
              {openAlert === `${a.type}:${a.key}` && <AlertStudents alert={a} id={`alert-students-${i}`} />}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Program placement rates (single-hue bars) ────────────────────────────── */
function ProgramPlacement({ rows }) {
  // Show every program; populated ones first, empty ones ("no students yet") last.
  const list = (rows || []).slice().sort((a, b) => (b.total - a.total) || (b.rate - a.rate));
  if (list.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No programs configured.</p>;
  }
  return (
    <div className="hs-rows">
      {list.map(p => (
        <div key={p.code}>
          <div className="hs-row-top">
            <span className="hs-row-name">{p.name}</span>
            {p.total > 0
              ? <span className="hs-row-val"><b>{p.rate}%</b> · {p.placed}/{p.total} placed</span>
              : <span className="hs-row-val" style={{ color: 'var(--ink-4)' }}>No students yet</span>}
          </div>
          <div className="hs-track"><div className="hs-fill" style={{ width: `${p.rate}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

/* ── Per-coordinator placed / not-yet (stacked) ───────────────────────────── */
function PCBreakdown({ rows }) {
  if (!rows || rows.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No students assigned to coordinators yet.</p>;
  }
  return (
    <>
      <div className="hs-legend">
        <span><i style={{ background: 'var(--teal)' }} /> Placed</span>
        <span><i style={{ background: 'var(--line-strong)' }} /> Not yet placed</span>
      </div>
      <div className="hs-rows" style={{ gap: 15 }}>
        {rows.map((pc, i) => {
          const rate = pc.total > 0 ? Math.round((pc.placed / pc.total) * 100) : 0;
          const initials = (pc.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
          return (
            <div key={i} className="hs-pc-row">
              <div className="hs-avatar">{initials}</div>
              <div className="hs-pc-body">
                <div className="hs-pc-name">
                  <span className="name">{pc.name}</span>
                  <span className="meta"><b>{pc.placed}</b> placed · {pc.notPlaced} not yet</span>
                </div>
                <div className="hs-stack" title={`${rate}% placed`}>
                  <div className="hs-stack-placed" style={{ width: `${rate}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ── Upcoming placement demand (unplaced, by start window) ────────────────── */
function DemandTimeline({ rows }) {
  const list = rows || [];
  const total = list.reduce((a, r) => a + r.count, 0);
  if (total === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No unplaced students starting in the next 90 days.</p>;
  }
  const max = Math.max(...list.map(r => r.count), 1);
  return (
    <div className="hs-rows">
      {list.map(r => (
        <div key={r.label}>
          <div className="hs-row-top">
            <span className="hs-row-name">
              {r.urgent && <span style={{ color: '#D14343', marginRight: 6 }}>●</span>}
              {r.label}
            </span>
            <span className="hs-row-val"><b>{r.count}</b> student{r.count !== 1 ? 's' : ''}</span>
          </div>
          <div className="hs-track">
            <div className="hs-fill" style={{ width: `${(r.count / max) * 100}%`, background: r.urgent ? '#D14343' : undefined }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Top cities by placement ──────────────────────────────────────────────── */
function TopCities({ rows }) {
  if (!rows || rows.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No placements recorded yet.</p>;
  }
  const max = Math.max(...rows.map(r => r.count), 1);
  const medals = ['#F5A623', '#C3CDD6', '#D9892A'];
  return (
    <div className="hs-rows">
      {rows.map((c, i) => (
        <div key={c.city} className="hs-city-row">
          <div className="hs-rank" style={{ background: medals[i] || 'var(--line-strong)' }}>{i + 1}</div>
          <div className="hs-city-body">
            <div className="hs-row-top">
              <span className="hs-row-name">{c.city}</span>
              <span className="hs-row-val">{c.count} placed</span>
            </div>
            <div className="hs-track"><div className="hs-fill" style={{ width: `${(c.count / max) * 100}%` }} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}
